// faceProfileBuilder — превращает данные грани (selectedFaceData) в
// полигональный профиль для extrudeSketchProfile.
//
// faceData: { bodyId, faceId, planeRef:{plane,offset,center}, normal:[x,y,z], points3d:[[x,y,z],...] }
//
// Возвращает: { kind:'polygon', verts:[{x,y},...], planeName, planeOffset, sign, center3d }
// или null если данные невалидные.
//
// 2D-координаты (x, y) в локальной системе replicad-плоскости:
//   'XY' — (point.x, point.y)
//   'XZ' — (point.x, point.z)  с zDir=(0,-1,0)
//   'YZ' — (point.y, point.z)  с zDir=(1,0,0)
//
// sign — направление выдавливания (+1 если нормаль грани сонаправлена с zDir
// плоскости, -1 если противоположна). Это важно для extrudeArrow и для того
// чтобы distance>0 = выдавливание наружу из грани.

export function buildFaceProfile(faceData) {
    if (!faceData) return null;
    const { planeRef, normal, points3d, holes3d } = faceData;
    if (!planeRef || !normal || !points3d || points3d.length < 3) return null;

    const plane = planeRef.plane;
    const offset = planeRef.offset || 0;

    let verts;
    let sign;
    let customPlane = null;

    if (plane === 'XY') {
        verts = points3d.map(([x, y]) => ({ x, y }));
        sign = (normal[2] >= 0) ? 1 : -1;
    } else if (plane === 'XZ') {
        verts = points3d.map(([x, , z]) => ({ x, y: z }));
        sign = (normal[1] >= 0) ? -1 : 1;
    } else if (plane === 'YZ') {
        verts = points3d.map(([, y, z]) => ({ x: y, y: z }));
        sign = (normal[0] >= 0) ? 1 : -1;
    } else if (plane === 'CUSTOM') {
        // Произвольная плоскость: проецируем 3D-точки на её локальную 2D
        // систему через xDir / yDir (yDir = normal × xDir).
        const o = planeRef.origin;       // [ox, oy, oz]
        const xd = planeRef.xDir;        // [xx, xy, xz]
        const n = planeRef.normal;       // [nx, ny, nz]
        // yDir = n × xDir
        const yd = [
            n[1] * xd[2] - n[2] * xd[1],
            n[2] * xd[0] - n[0] * xd[2],
            n[0] * xd[1] - n[1] * xd[0],
        ];
        verts = points3d.map((p) => {
            const dx = p[0] - o[0];
            const dy = p[1] - o[1];
            const dz = p[2] - o[2];
            return {
                x: dx * xd[0] + dy * xd[1] + dz * xd[2],
                y: dx * yd[0] + dy * yd[1] + dz * yd[2],
            };
        });
        sign = 1; // в Plane'е zDir = normal, distance>0 идёт по normal
        customPlane = { origin: o, xDir: xd, normal: n };
    } else {
        return null;
    }

    // Гарантируем CCW-ориентацию (replicad ожидает counter-clockwise).
    if (signedArea(verts) < 0) verts.reverse();

    /*
     * ДЫРКИ ГРАНИ. У кольцевой грани (торец трубы, дно коробки с отверстием)
     * есть внутренние контуры. Без них «Выдавить грань» строит сплошной круг
     * вместо кольца, и вырезание падает в OCCT.
     *
     * Проецируем их ТОЙ ЖЕ формулой, что и внешний контур: иначе дырка
     * окажется не на своём месте. Ориентация у дырок противоположная
     * внешнему контуру — так того требует replicad.
     */
    let holes = null;
    if (Array.isArray(holes3d) && holes3d.length) {
        holes = holes3d
            .map((h) => projectContour(h, plane, planeRef))
            .filter((hv) => hv && hv.length >= 3)
            .map((hv) => {
                if (signedArea(hv) > 0) hv.reverse();
                return { kind: 'polygon', verts: hv };
            });
        if (!holes.length) holes = null;
    }

    return {
        kind: 'polygon',
        verts,
        holes,              // null или [{ kind:'polygon', verts }]
        planeName: plane,
        planeOffset: offset,
        customPlane,        // null для XY/XZ/YZ, объект для CUSTOM
        sign,
        center3d: planeRef.center,
    };
}


/**
 * Спроецировать 3D-контур на локальную 2D-систему плоскости.
 *
 * Повторяет ту же логику, что и для внешнего контура: XY берёт (x, y),
 * XZ — (x, z), YZ — (y, z), CUSTOM раскладывает по xDir/yDir.
 */
function projectContour(pts, plane, planeRef) {
    if (!Array.isArray(pts) || pts.length < 3) return null;
    if (plane === 'XY') return pts.map(([x, y]) => ({ x, y }));
    if (plane === 'XZ') return pts.map(([x, , z]) => ({ x, y: z }));
    if (plane === 'YZ') return pts.map(([, y, z]) => ({ x: y, y: z }));
    if (plane === 'CUSTOM') {
        const o = planeRef.origin, xd = planeRef.xDir, n = planeRef.normal;
        const yd = [
            n[1] * xd[2] - n[2] * xd[1],
            n[2] * xd[0] - n[0] * xd[2],
            n[0] * xd[1] - n[1] * xd[0],
        ];
        return pts.map((p) => {
            const dx = p[0] - o[0], dy = p[1] - o[1], dz = p[2] - o[2];
            return {
                x: dx * xd[0] + dy * xd[1] + dz * xd[2],
                y: dx * yd[0] + dy * yd[1] + dz * yd[2],
            };
        });
    }
    return null;
}

function signedArea(verts) {
    let s = 0;
    for (let i = 0; i < verts.length; i++) {
        const j = (i + 1) % verts.length;
        s += verts[i].x * verts[j].y - verts[j].x * verts[i].y;
    }
    return s / 2;
}
